import type { DTOBlock } from '@/api/generated-catch-backend/model/dTOBlock';
import { motion } from 'motion/react';
import { INTERSTITIAL_ICON_REGISTRY } from '@/components/icons/interstitial-icons';
import { PortableText } from '@/app/components/questionnaireV2/components/portable-text';
import { StickyFooterBar } from '../../../sticky-footer-bar/sticky-footer-bar';
import {
  BackgroundAsset,
  FRAME_TRANSITION_DURATION,
  getGraphicStyles,
  GraphicLottie,
  InterstitialTextContentBlock,
} from './interstitial-shared';

// Generic graphic type that works for both questionnaire and risk-score-reveal contexts
export interface BaseImageOrGraphType {
  type: 'image' | 'lottie' | 'icon' | 'custom';
  asset?: string | null;
  height?: number | null;
  width?: number | null;
  customGraphicName?: string | null;
}

export interface BaseImageInterstitialProps {
  // Content
  titleAbove?: string | null;
  subtitleAbove?: DTOBlock[] | null; // PortableText content
  titleBelow?: string | null;
  subtitleBelow?: DTOBlock[] | null;

  // Graphics
  primaryGraphic?: BaseImageOrGraphType | null; // Main graphic
  secondaryGraphic?: BaseImageOrGraphType | null; // Optional below graphic

  // Background
  backgroundAsset?: string | null;
  backgroundOpacity?: number;

  // Interaction
  onNext?: () => void;
  onBack?: () => void;
  isBackDisabled?: boolean;
  buttonText?: string;

  // Styling
  card?: string | null;
  className?: string;

  // Custom button renderer for different contexts
  renderButton?: (onNext: () => void, text: string) => React.ReactNode;
  renderCustomGraphic?: () => React.ReactNode;

  // When true, skip rendering the internal CTA button (page-level footer handles it)
  hideInternalCta?: boolean;

  // Testing
  'data-testid'?: string;
}

// Helper component to render graphics
const BaseMainGraphic = ({
  imageOrGraph,
  renderCustomGraphic,
}: {
  imageOrGraph: BaseImageOrGraphType | null | undefined;
  renderCustomGraphic?: () => React.ReactNode;
}) => {
  if (!imageOrGraph) return null;

  const isLottie = imageOrGraph.type === 'lottie';
  const dynamicStyles = getGraphicStyles(imageOrGraph);

  if (isLottie && imageOrGraph.asset) {
    return (
      <div className="flex w-full justify-center">
        <div className="w-full rounded-lg" style={dynamicStyles} data-content-type="lottie">
          <GraphicLottie srcUrl={imageOrGraph.asset} />
        </div>
      </div>
    );
  }

  if (imageOrGraph.type === 'image' && imageOrGraph.asset) {
    const isCircular = imageOrGraph.width && imageOrGraph.height && imageOrGraph.width === imageOrGraph.height;
    return (
      <div className="flex w-full justify-center">
        <img
          src={imageOrGraph.asset}
          alt="Interstitial graphic"
          className={isCircular ? 'rounded-full object-cover' : 'w-full rounded-lg object-contain'}
          style={isCircular ? { width: imageOrGraph.width!, height: imageOrGraph.height! } : dynamicStyles}
          data-content-type="image"
        />
      </div>
    );
  }

  if (imageOrGraph.type === 'icon' && imageOrGraph.asset) {
    const IconComponent = INTERSTITIAL_ICON_REGISTRY[imageOrGraph.asset];
    if (IconComponent) {
      const iconWidth = imageOrGraph.width ?? 80;
      const iconHeight = imageOrGraph.height ?? 80;
      return (
        <div className="flex w-full justify-center" data-content-type="icon">
          <IconComponent width={iconWidth} height={iconHeight} className="text-white" />
        </div>
      );
    }
    return null;
  }

  // Handle custom graphics - could be extended for risk score reveal custom graphics
  if (imageOrGraph.type === 'custom' && imageOrGraph.customGraphicName && renderCustomGraphic) {
    return renderCustomGraphic();
  }

  return null;
};

// Wrapper component for text content with consistent styling
const BaseTextContentBlock = ({
  title,
  subtitle,
  containerClassName = '',
}: {
  title: string | null | undefined;
  subtitle: DTOBlock[] | null | undefined;
  containerClassName?: string;
}) => {
  return (
    <div className={`w-full max-w-lg pb-6 md:max-w-xl lg:max-w-2xl ${containerClassName}`}>
      <InterstitialTextContentBlock
        title={title}
        subtitle={subtitle}
        containerClassName=""
        textColorClass="text-white"
      />
    </div>
  );
};

export const BaseImageInterstitial = ({
  titleAbove,
  subtitleAbove,
  titleBelow,
  subtitleBelow,
  primaryGraphic,
  secondaryGraphic,
  backgroundAsset,
  backgroundOpacity = 0.6,
  card,
  onNext,
  onBack,
  isBackDisabled = false,
  buttonText = 'Continue',
  className = '',
  renderButton,
  renderCustomGraphic,
  hideInternalCta = false,
  'data-testid': testId,
}: BaseImageInterstitialProps) => {
  return (
    <motion.div
      className={`relative isolate flex w-full flex-col overflow-hidden overscroll-contain bg-gradient-to-r from-[#204660] to-[#0e1d27] font-['Inter'] ${className}`}
      style={{ height: 'var(--app-height, 100dvh)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: FRAME_TRANSITION_DURATION }}
    >
      {/* Default gradient background */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#204660] to-[#0e1d27]" />

      {/* Background asset (video or image) */}
      <BackgroundAsset backgroundAsset={backgroundAsset} classNamePrefix="base-image-interstitial" />

      {/* Background overlay for opacity control */}
      <div
        className="absolute inset-0 z-[3] bg-black"
        style={{
          opacity: backgroundOpacity,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)', // Safari support
        }}
      />

      {/* Main scrollable content area */}
      <div className="relative z-10 flex flex-1 overflow-y-auto overscroll-contain" data-testid={testId}>
        <div className="flex w-full flex-col items-center px-6 py-8">
          {/* Top spacer for safe area - reduced padding */}
          <div className="pt-safe-offset-8 md:pt-safe-offset-6" />

          {/* Text above graphic */}
          {(titleAbove || subtitleAbove) && (
            <BaseTextContentBlock title={titleAbove} subtitle={subtitleAbove} containerClassName="pb-6" />
          )}

          {/* Primary graphic in upper-middle */}
          {primaryGraphic && (
            <div className="flex w-full max-w-md justify-center py-4">
              <BaseMainGraphic imageOrGraph={primaryGraphic} renderCustomGraphic={renderCustomGraphic} />
            </div>
          )}

          {/* Text below primary graphic */}
          {(titleBelow || subtitleBelow) && (
            <div className="w-full max-w-lg pt-6 md:max-w-xl lg:max-w-2xl">
              {titleBelow && (
                <BaseTextContentBlock title={titleBelow} subtitle={null} />
              )}
              {subtitleBelow && card === 'glass' ? (
                <div className="mx-auto mt-4 w-fit space-y-3 rounded-2xl border border-white/20 bg-white/10 p-6 text-left text-lg leading-relaxed text-white backdrop-blur-xl">
                  <PortableText content={subtitleBelow} />
                </div>
              ) : subtitleBelow ? (
                <BaseTextContentBlock title={null} subtitle={subtitleBelow} />
              ) : null}
            </div>
          )}

          {/* Secondary graphic */}
          {secondaryGraphic && (
            <div className="flex w-full max-w-lg justify-center py-4">
              <BaseMainGraphic imageOrGraph={secondaryGraphic} />
            </div>
          )}

          {/* Bottom padding for content - reduced */}
          <div className="pb-4" />
        </div>
      </div>

      {!hideInternalCta && (onNext || onBack) && (
        <StickyFooterBar variant="dark" onBack={onBack} isBackDisabled={isBackDisabled}>
          {onNext && (
            renderButton ? (
              renderButton(onNext, buttonText)
            ) : (
              <button onClick={onNext} className="btn-action">
                <span>{buttonText}</span>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )
          )}
        </StickyFooterBar>
      )}
    </motion.div>
  );
};
